<template>
  <div class="router-editor">
    <div class="form-item pb-0">
      <div class="form-item__label">{{ $t("satellites.routers.name") }}</div>
      <div class="form-item__field">
        <input v-model="name" type="text" :maxlength="nameMaxLength" :placeholder="$t('satellites.routers.name_placeholder')" :class="{ invalid: !!nameError }" @input="clearServer('name')" />
        <small v-if="nameError" class="error">{{ nameError }}</small>
      </div>
    </div>

    <div v-if="!options.landings.length" class="router-editor__notice mt-3">
      {{ $t("satellites.routers.no_landings") }}
      <router-link :to="{ name: 'SatelliteLandings' }">{{ $t("satellites.routers.go_landings") }}</router-link>
    </div>

    <div v-for="(route, index) in routes" :key="route.key" class="router-editor__route">
      <div class="router-editor__route-head">
        <span class="router-editor__route-title">{{ $t("satellites.routers.route_title", { number: index + 1 }) }}</span>
        <button v-if="routes.length > 1" type="button" class="controls__btn router-editor__remove" :title="$t('satellites.routers.remove_route')" @click.prevent="removeRoute(index)">
          <svg v-svg symbol="delete" size="0 0 15 15" role="info" />
        </button>
      </div>

      <div class="router-editor__grid">
        <div class="form-item pb-0">
          <div class="form-item__label">{{ $t("satellites.routers.path") }}</div>
          <div class="form-item__field">
            <input
              v-model="route.path"
              type="text"
              :maxlength="limits.path_length"
              :placeholder="$t('satellites.routers.path_placeholder')"
              :class="{ invalid: !!routeError(index, 'path') }"
              @input="clearServer(`routes.${index}.path`)"
            />
            <small v-if="routeError(index, 'path')" class="error">{{ routeError(index, "path") }}</small>
          </div>
        </div>

        <div class="form-item pb-0">
          <div class="form-item__label">{{ $t("satellites.routers.strategy") }}</div>
          <div class="form-item__field">
            <custom-multiselect
              :value="strategyOption(route)"
              :list="strategyOptions"
              :allowEmpty="false"
              :class="{ invalid: !!routeError(index, 'strategy') }"
              @input="(option) => setStrategy(index, option)"
            >
              <template v-slot:block="props">
                <span>{{ props.value.title }}</span>
              </template>
              <template v-slot:list="props">
                <span>{{ props.value.title }}</span>
              </template>
            </custom-multiselect>
            <small v-if="routeError(index, 'strategy')" class="error">{{ routeError(index, "strategy") }}</small>
            <small v-else class="router-editor__muted d-block mt-1">{{ $t(`satellites.routers.strategies_hint.${route.strategy}`) }}</small>
          </div>
        </div>
      </div>

      <div class="form-item pb-0">
        <div class="form-item__label">{{ $t("satellites.routers.platform") }}</div>
        <div class="router-editor__modes">
          <button
            v-for="mode in platformModes"
            :key="mode"
            type="button"
            class="router-editor__mode"
            :class="{ _active: route.platformMode === mode }"
            @click.prevent="setPlatformMode(index, mode)"
          >
            {{ $t(`satellites.routers.platform_modes.${mode}`) }}
          </button>
        </div>
        <div class="form-item__field mt-2">
          <template v-if="route.platformMode === 'fixed'">
            <custom-multiselect
              :value="route.platform || platformPlaceholder"
              :list="platformOptions"
              :allowEmpty="false"
              searchBy="title"
              :class="{ invalid: !!routeError(index, 'platform') }"
              @input="(option) => setPlatform(index, option)"
            >
              <template v-slot:block="props">
                <span :class="{ 'router-editor__placeholder': props.value.id === null }">{{ props.value.title }}</span>
              </template>
              <template v-slot:list="props">
                <span>{{ props.value.title }}</span>
              </template>
            </custom-multiselect>
            <div v-if="!options.platforms.length" class="router-editor__muted mt-1">
              {{ $t("satellites.routers.no_platforms") }}
              <router-link :to="{ name: 'SatellitePlatforms' }">{{ $t("satellites.routers.go_platforms") }}</router-link>
            </div>
            <div v-else-if="!routeError(index, 'platform')" class="router-editor__muted mt-1">{{ $t("satellites.routers.platforms_only") }}</div>
          </template>
          <template v-else>
            <input
              v-model="route.platformParam"
              type="text"
              maxlength="32"
              :placeholder="defaults.platform_param"
              :class="{ invalid: !!routeError(index, 'platform') }"
              @input="clearServer(`routes.${index}.platform`)"
            />
            <small v-if="!routeError(index, 'platform')" class="router-editor__muted d-block mt-1">{{ $t("satellites.routers.platform_param_hint") }}</small>
          </template>
          <small v-if="routeError(index, 'platform')" class="error">{{ routeError(index, "platform") }}</small>
        </div>
      </div>

      <div class="form-item pb-0">
        <div class="form-item__label">{{ $t("satellites.routers.sub_params") }}</div>
        <div class="router-editor__subs">
          <label v-for="(sub, subIndex) in route.subParams" :key="subIndex" class="router-editor__sub">
            <span class="router-editor__sub-label">sub{{ subIndex + 1 }}</span>
            <input
              v-model="route.subParams[subIndex]"
              type="text"
              maxlength="32"
              :placeholder="$t('satellites.routers.sub_disabled')"
              :class="{ invalid: subInvalid(index, subIndex) }"
              @input="clearServer(`routes.${index}.sub_params`)"
            />
          </label>
        </div>
        <small v-if="routeError(index, 'sub_params')" class="error">{{ routeError(index, "sub_params") }}</small>
        <small v-else class="router-editor__muted d-block mt-1">{{ $t("satellites.routers.sub_params_hint") }}</small>
      </div>

      <div class="form-item pb-0">
        <div class="form-item__label">{{ $t("satellites.routers.landings", { number: limits.landings_per_route }) }}</div>
        <div class="form-item__field router-editor__landings">
          <custom-multiselect
            v-model="route.landings"
            :list="landingOptions"
            :multiply="true"
            :maxLength="limits.landings_per_route"
            searchBy="title"
            :class="{ invalid: !!routeError(index, 'landing_ids') }"
            @input="clearServer(`routes.${index}.landing_ids`)"
          >
            <template v-slot:block="props">
              <span>{{ props.value.title }}</span>
            </template>
            <template v-slot:list="props">
              <span>{{ props.value.title }}</span>
            </template>
          </custom-multiselect>
          <span v-if="!route.landings.length" class="router-editor__landings-placeholder">{{ $t("satellites.routers.choose_landings") }}</span>
          <small v-if="routeError(index, 'landing_ids')" class="error">{{ routeError(index, "landing_ids") }}</small>
        </div>
      </div>

      <div class="router-editor__example">
        <div class="router-editor__example-label">{{ $t("satellites.routers.example") }}</div>
        <code class="router-editor__example-url">{{ exampleUrl(route) }}</code>
        <div class="router-editor__muted mt-1">{{ exampleHint(route) }}</div>
      </div>
    </div>

    <small v-if="routesError" class="error d-block mt-2">{{ routesError }}</small>

    <button type="button" class="btn _border _green router-editor__add mt-3" :disabled="routes.length >= limits.routes" @click.prevent="addRoute">
      + {{ $t("satellites.routers.add_route") }}
    </button>
    <small v-if="routes.length >= limits.routes" class="router-editor__muted d-block mt-1">{{ $t("satellites.routers.routes_limit", { number: limits.routes }) }}</small>

    <slot name="extra" />

    <div class="router-editor__system">
      <div class="router-editor__system-title">{{ $t("satellites.routers.system_routes.title") }}</div>
      <div class="router-editor__notice mt-2">{{ $t("satellites.routers.system_routes.note") }}</div>

      <div class="form-item pb-0 mt-3">
        <div class="form-item__label">{{ $t("satellites.routers.system_routes.service_worker") }}</div>
        <div v-for="(worker, index) in serviceWorkers" :key="worker.key" class="router-editor__system-path">
          <div class="form-item__field">
            <div class="router-editor__system-row">
              <input
                v-model="worker.path"
                type="text"
                :maxlength="limits.path_length"
                :disabled="worker.saved"
                :placeholder="worker.saved ? '' : '/push-sw.js'"
                :class="{ invalid: !!systemRouteError(index) }"
                @input="clearServer(`system_routes.${index}.path`)"
              />
              <span v-if="worker.saved" class="router-editor__lock" :title="$t('satellites.routers.system_routes.saved_path')">
                <svg v-svg symbol="lock" size="0 0 32 33" role="presentation" />
              </span>
              <button
                v-else-if="serviceWorkers.length > 1"
                type="button"
                class="controls__btn router-editor__remove"
                :title="$t('satellites.routers.system_routes.remove_path')"
                @click.prevent="removeServiceWorker(index)"
              >
                <svg v-svg symbol="delete" size="0 0 15 15" role="info" />
              </button>
            </div>
            <small v-if="systemRouteError(index)" class="error">{{ systemRouteError(index) }}</small>
          </div>
        </div>
        <small class="router-editor__muted d-block mt-1">{{ $t("satellites.routers.system_routes.service_worker_hint") }}</small>
        <button type="button" class="btn _border _green router-editor__add mt-2" :disabled="serviceWorkers.length >= limits.service_worker_paths" @click.prevent="addServiceWorker">
          + {{ $t("satellites.routers.system_routes.add_path") }}
        </button>
        <small v-if="serviceWorkers.length >= limits.service_worker_paths" class="router-editor__muted d-block mt-1">
          {{ $t("satellites.routers.system_routes.service_worker_limit", { number: limits.service_worker_paths }) }}
        </small>
      </div>

      <div class="form-item pb-0 mt-3">
        <div class="form-item__label">{{ $t("satellites.routers.system_routes.monetization") }}</div>
        <div class="form-item__field">
          <input
            v-model="monetizationPath"
            type="text"
            :maxlength="limits.path_length"
            :placeholder="defaultMonetizationPath"
            :class="{ invalid: !!systemRouteError(serviceWorkers.length) }"
            @input="clearServer(`system_routes.${serviceWorkers.length}.path`)"
          />
          <small v-if="systemRouteError(serviceWorkers.length)" class="error">{{ systemRouteError(serviceWorkers.length) }}</small>
          <small v-else class="router-editor__muted d-block mt-1">{{ $t("satellites.routers.system_routes.monetization_hint") }}</small>
        </div>
      </div>

      <small v-if="systemRoutesError" class="error d-block mt-2">{{ systemRoutesError }}</small>
    </div>

    <p v-if="inUse" class="router-editor__muted mt-3">{{ $t("satellites.routers.edit_note") }}</p>
    <div v-if="error" class="router-editor__error mt-3">{{ error }}</div>

    <div class="text-center">
      <button class="btn mt-4 mr-3 _bg-gray" @click.prevent="$emit('cancel')">{{ $t("cancel") }}</button>
      <button class="btn mt-4 _bg-green router-editor__submit" :disabled="saving" @click.prevent="save">
        <img v-if="saving" src="@/assets/images/rolling-button.svg" class="loader-button" alt="loader" />
        {{ router ? $t("save") : $t("satellites.routers.create") }}
      </button>
    </div>
  </div>
</template>
<script>
import {
  DEFAULT_ROUTER_OPTIONS,
  normalizePath,
  PLATFORM_FIXED,
  PLATFORM_PARAM,
  ROUTER_NAME_MAX_LENGTH,
  STRATEGIES,
  SUB_PARAMS_COUNT,
  SYSTEM_ROUTE_MONETIZATION,
  SYSTEM_ROUTE_SERVICE_WORKER,
  validateLandingIds,
  validatePath,
  validatePlatform,
  validateRouterName,
  validateSubParams,
  validateSystemPath,
} from "@/components/Satellites/routers";

let routeKey = 0;

/**
 * Form of a satellite router: a name, a list of routes "path → landings" and the system routes (service worker paths
 * and the monetization script path).
 *
 * Emits `save` with the request body { name, routes, system_routes } and `cancel`. Server violations (keyed by "name",
 * "routes", "routes.{index}.{field}", "system_routes" or "system_routes.{index}.{field}") come back through the
 * `violations` prop; a violation is hidden once its field is edited. System routes are sent as the service workers
 * followed by the monetization script, so their indexes match the form.
 */
export default {
  name: "RouterEditor",
  props: {
    // Router being edited, null for a new one
    router: {
      type: Object,
      default: null,
    },
    // data of /satellite-routers/options
    options: {
      type: Object,
      required: true,
    },
    violations: {
      type: Object,
      default: () => ({}),
    },
    // An error that is not about a field
    error: {
      type: String,
      default: "",
    },
    saving: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      name: "",
      routes: [],
      // [{ key, path, saved }]: saved paths are stored on the server and can't be changed
      serviceWorkers: [],
      monetizationPath: "",
      serverViolations: {},
      // Errors about missing values are shown after the first save attempt only
      submitted: false,
      nameMaxLength: ROUTER_NAME_MAX_LENGTH,
      platformModes: [PLATFORM_FIXED, PLATFORM_PARAM],
    };
  },
  computed: {
    defaults() {
      const defaults = this.options.defaults || {};
      const fallback = DEFAULT_ROUTER_OPTIONS.defaults;
      return {
        platform_param: defaults.platform_param || fallback.platform_param,
        sub_params: Array.isArray(defaults.sub_params) && defaults.sub_params.length === SUB_PARAMS_COUNT ? defaults.sub_params : fallback.sub_params,
        strategy: defaults.strategy || fallback.strategy,
        system_routes: Array.isArray(defaults.system_routes) && defaults.system_routes.length ? defaults.system_routes : fallback.system_routes,
      };
    },
    defaultServiceWorkerPath() {
      return this.systemPath(this.defaults.system_routes, SYSTEM_ROUTE_SERVICE_WORKER);
    },
    defaultMonetizationPath() {
      return this.systemPath(this.defaults.system_routes, SYSTEM_ROUTE_MONETIZATION);
    },
    limits() {
      return { ...DEFAULT_ROUTER_OPTIONS.limits, ...(this.options.limits || {}) };
    },
    inUse() {
      const usedBy = this.router && this.router.used_by;
      return !!usedBy && (usedBy.domains || []).length > 0;
    },
    platformOptions() {
      return this.options.platforms.map((platform) => this.platformOption(platform.id));
    },
    platformPlaceholder() {
      return { id: null, title: this.$t("satellites.routers.choose_platform") };
    },
    landingOptions() {
      return this.options.landings.map((landing) => ({ id: landing.id, title: landing.name }));
    },
    strategyOptions() {
      return STRATEGIES.map((value) => ({ value, title: this.$t(`satellites.routers.strategies.${value}`) }));
    },
    body() {
      return {
        name: this.name.trim(),
        routes: this.routes.map((route) => ({
          path: normalizePath(route.path),
          platform: this.platformOf(route),
          sub_params: route.subParams.map((name) => name.trim()),
          strategy: route.strategy,
          landing_ids: route.landings.map((landing) => landing.id),
        })),
        system_routes: [
          ...this.serviceWorkers.map((worker) => ({ type: SYSTEM_ROUTE_SERVICE_WORKER, path: normalizePath(worker.path) })),
          { type: SYSTEM_ROUTE_MONETIZATION, path: normalizePath(this.monetizationPath) },
        ],
      };
    },
    // Client side errors: { name, routes: [{ path, platform, sub_params: { error, slots }, landing_ids }], system_routes: [path error] }
    clientErrors() {
      const paths = [];
      const routePaths = this.body.routes.map((route) => route.path);
      const systemPaths = [];
      return {
        name: validateRouterName(this.name),
        system_routes: this.body.system_routes.map((route) => {
          const error = validateSystemPath(route.path, this.limits.path_length, routePaths, systemPaths);
          systemPaths.push(route.path);
          return error;
        }),
        routes: this.body.routes.map((route) => {
          const path = validatePath(route.path, this.limits.path_length, paths);
          paths.push(route.path);
          return {
            path,
            platform: validatePlatform(route.platform),
            sub_params: validateSubParams(route.sub_params, route.platform),
            landing_ids: validateLandingIds(route.landing_ids, this.limits.landings_per_route),
          };
        }),
      };
    },
    hasClientErrors() {
      const { name, routes, system_routes: systemRoutes } = this.clientErrors;
      return !!name || routes.some((route) => route.path || route.platform || route.sub_params.error || route.landing_ids) || systemRoutes.some(Boolean);
    },
    nameError() {
      return this.serverError("name") || this.translate(this.clientErrors.name);
    },
    routesError() {
      return this.serverError("routes");
    },
    systemRoutesError() {
      return this.serverError("system_routes");
    },
  },
  watch: {
    violations: {
      handler(violations) {
        this.serverViolations = { ...(violations || {}) };
      },
      immediate: true,
    },
  },
  created() {
    if (this.router) {
      this.name = this.router.name;
      this.routes = (this.router.routes || []).map((route) => this.routeForm(route));
    }
    if (!this.routes.length) this.routes = [this.routeForm({ path: "/" })];

    const saved = this.router && Array.isArray(this.router.system_routes) && this.router.system_routes.length;
    const systemRoutes = saved ? this.router.system_routes : this.defaults.system_routes;
    this.serviceWorkers = systemRoutes.filter((route) => route.type === SYSTEM_ROUTE_SERVICE_WORKER).map((route) => this.serviceWorkerForm(route.path, !!saved));
    if (!this.serviceWorkers.length) this.serviceWorkers = [this.serviceWorkerForm(this.defaultServiceWorkerPath, false)];
    this.monetizationPath = this.systemPath(systemRoutes, SYSTEM_ROUTE_MONETIZATION) || this.defaultMonetizationPath;
  },
  methods: {
    // Form state of a route of the API
    routeForm(route) {
      const platform = route.platform || { mode: PLATFORM_PARAM, param: this.defaults.platform_param };
      const subParams = Array.isArray(route.sub_params) && route.sub_params.length === SUB_PARAMS_COUNT ? route.sub_params : this.defaults.sub_params;
      return {
        key: ++routeKey,
        path: route.path || "",
        platformMode: platform.mode === PLATFORM_FIXED ? PLATFORM_FIXED : PLATFORM_PARAM,
        platform: platform.mode === PLATFORM_FIXED && Number.isInteger(platform.id) ? this.platformOption(platform.id) : null,
        platformParam: platform.mode === PLATFORM_PARAM && platform.param ? platform.param : this.defaults.platform_param,
        subParams: [...subParams],
        strategy: route.strategy || this.defaults.strategy,
        landings: (route.landing_ids || []).map((id) => this.landingOption(id)),
      };
    },
    serviceWorkerForm(path, saved) {
      return { key: ++routeKey, path: path || "", saved };
    },
    // Path of the first system route of the type
    systemPath(systemRoutes, type) {
      const route = (systemRoutes || []).find((systemRoute) => systemRoute.type === type);
      return route ? route.path : "";
    },
    addServiceWorker() {
      if (this.serviceWorkers.length >= this.limits.service_worker_paths) return;
      this.serviceWorkers.push(this.serviceWorkerForm("", false));
      this.clearSystemViolations();
    },
    removeServiceWorker(index) {
      if (this.serviceWorkers[index].saved) return;
      this.serviceWorkers.splice(index, 1);
      this.clearSystemViolations();
    },
    // Indexes of the system routes shift when a service worker path is added or removed
    clearSystemViolations() {
      this.serverViolations = Object.fromEntries(Object.entries(this.serverViolations).filter(([key]) => !key.startsWith("system_routes")));
    },
    systemRouteError(index) {
      const server = this.serverError(`system_routes.${index}.path`) || this.serverError(`system_routes.${index}.type`);
      if (server) return server;
      return this.translate(this.clientErrors.system_routes[index]);
    },
    platformOption(id) {
      const platform = this.options.platforms.find((option) => option.id === id);
      return { id, title: platform ? `${platform.name} (${id})` : `#${id}` };
    },
    landingOption(id) {
      const landing = this.options.landings.find((option) => option.id === id);
      return { id, title: landing ? landing.name : this.$t("satellites.routers.unknown_landing") };
    },
    platformOf(route) {
      if (route.platformMode === PLATFORM_FIXED) return { mode: PLATFORM_FIXED, id: route.platform ? route.platform.id : null };
      return { mode: PLATFORM_PARAM, param: route.platformParam.trim() };
    },
    strategyOption(route) {
      return this.strategyOptions.find((option) => option.value === route.strategy) || { value: route.strategy, title: route.strategy };
    },
    setStrategy(index, option) {
      this.routes[index].strategy = option.value;
      this.clearServer(`routes.${index}.strategy`);
    },
    setPlatformMode(index, mode) {
      this.routes[index].platformMode = mode;
      this.clearServer(`routes.${index}.platform`);
      this.clearServer(`routes.${index}.sub_params`);
    },
    setPlatform(index, option) {
      this.routes[index].platform = option.id === null ? null : option;
      this.clearServer(`routes.${index}.platform`);
    },
    addRoute() {
      if (this.routes.length >= this.limits.routes) return;
      this.routes.push(this.routeForm({ path: "" }));
      this.clearServer("routes");
    },
    removeRoute(index) {
      this.routes.splice(index, 1);
      // Violations of the next routes are keyed by the old indexes
      this.serverViolations = Object.fromEntries(Object.entries(this.serverViolations).filter(([key]) => !key.startsWith("routes")));
    },
    clearServer(key) {
      if (key in this.serverViolations) this.$delete(this.serverViolations, key);
    },
    serverError(key) {
      const messages = this.serverViolations[key];
      return Array.isArray(messages) && messages.length ? messages.join(" ") : "";
    },
    translate(error) {
      if (!error) return "";
      const params = error[1] || {};
      if (params.required && !this.submitted) return "";
      return this.$t(error[0], params);
    },
    routeError(index, field) {
      const server = this.serverError(`routes.${index}.${field}`);
      if (server) return server;
      const errors = this.clientErrors.routes[index];
      if (!errors || !(field in errors)) return "";
      return this.translate(field === "sub_params" ? errors.sub_params.error : errors[field]);
    },
    subInvalid(index, subIndex) {
      if (this.serverError(`routes.${index}.sub_params`)) return true;
      const errors = this.clientErrors.routes[index];
      return !!errors && errors.sub_params.slots.includes(subIndex);
    },
    exampleUrl(route) {
      const path = normalizePath(route.path) || "/";
      const query = [];
      const platform = this.platformOf(route);
      if (platform.mode === PLATFORM_PARAM && platform.param) query.push(`${platform.param}=${this.$t("satellites.routers.example_platform")}`);
      route.subParams.forEach((name, i) => {
        if (name.trim()) query.push(`${name.trim()}=<sub${i + 1}>`);
      });
      return `https://${this.$t("satellites.routers.example_domain")}${path}${query.length ? `?${query.join("&")}` : ""}`;
    },
    exampleHint(route) {
      const subs = route.subParams.some((name) => name.trim());
      if (route.platformMode === PLATFORM_PARAM) {
        return this.$t(subs ? "satellites.routers.example_hint_param" : "satellites.routers.example_hint_param_only", { param: route.platformParam.trim() || "?" });
      }
      return this.$t(subs ? "satellites.routers.example_hint_fixed" : "satellites.routers.example_hint_none");
    },
    save() {
      this.submitted = true;
      if (this.saving || this.hasClientErrors) return;
      this.$emit("save", this.body);
    },
  },
};
</script>
<style lang="scss" scoped>
.router-editor__route {
  margin-top: 20px;
  padding: 16px 20px 20px;
  border: 1px solid #e8e8ec;
  border-radius: 12px;
}

.router-editor__route-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 24px;
}

.router-editor__route-title {
  font-size: 15px;
  font-weight: 600;
  color: #141414;
}

.router-editor__remove {
  background: transparent;

  &:focus {
    outline: none;
  }
}

.router-editor__grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0 16px;

  @media screen and (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
}

.router-editor__modes {
  display: inline-flex;
  flex-wrap: wrap;
  padding: 3px;
  border-radius: 10px;
  background: #f1f1f4;
}

.router-editor__mode {
  padding: 6px 14px;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  color: #6f6f78;
  background: transparent;
  cursor: pointer;

  &:focus {
    outline: none;
  }

  &._active {
    color: #317475;
    background: #ffffff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  }
}

.router-editor__subs {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px 12px;

  @media screen and (min-width: 768px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

.router-editor__sub {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 0;

  input {
    width: 100%;
  }
}

.router-editor__sub-label {
  font-size: 12px;
  font-weight: 500;
  color: #9b9ba4;
}

.router-editor__landings {
  position: relative;
}

.router-editor__landings-placeholder {
  position: absolute;
  top: 0;
  left: 12px;
  display: flex;
  align-items: center;
  height: 45px;
  font-size: 14px;
  color: #9b9ba4;
  pointer-events: none;
}

.router-editor__placeholder {
  color: #9b9ba4;
}

.router-editor__example {
  margin-top: 16px;
  padding: 12px 14px;
  border-radius: 10px;
  background: #f7f7f9;
}

.router-editor__example-label {
  font-size: 12px;
  font-weight: 500;
  color: #9b9ba4;
}

.router-editor__example-url {
  display: block;
  margin-top: 4px;
  font-size: 13px;
  color: #141414;
  word-break: break-all;
  white-space: normal;
}

.router-editor__muted {
  font-size: 13px;
  color: #9b9ba4;
}

.router-editor__notice {
  padding: 10px 14px;
  border-radius: 10px;
  font-size: 13px;
  color: #a86a00;
  background: #fff4dc;
}

.router-editor__error {
  font-size: 14px;
  color: #f14343;
  text-align: center;
}

.router-editor__add {
  height: 40px;
  padding: 0 16px;
}

.router-editor__system {
  margin-top: 28px;
  padding: 16px 20px 20px;
  border: 1px dashed #c9c9d1;
  border-radius: 12px;
}

.router-editor__system-title {
  font-size: 15px;
  font-weight: 600;
  color: #141414;
}

.router-editor__system-path + .router-editor__system-path {
  margin-top: 8px;
}

.router-editor__system-row {
  display: flex;
  align-items: center;
  gap: 8px;

  input {
    flex: 1;
    min-width: 0;
  }
}

.router-editor__lock {
  display: inline-flex;
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  color: #9b9ba4;

  svg {
    width: 100%;
    height: 100%;
    fill: currentColor;
  }
}

.router-editor__submit {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.custom-multiselect.invalid ::v-deep .custom-multiselect-wrapper {
  box-shadow: inset 0 0 0 1px #f14343;
}

body.dark {
  .router-editor__route {
    border-color: #2f2f35;
  }

  .router-editor__system {
    border-color: #2f2f35;
  }

  .router-editor__route-title,
  .router-editor__system-title,
  .router-editor__example-url {
    color: #dddddd;
  }

  .router-editor__modes {
    background: #2f2f35;
  }

  .router-editor__mode {
    color: #b5b5bd;

    &._active {
      color: #6fc3c4;
      background: #151515;
    }
  }

  .router-editor__example {
    background: #151515;
  }

  .router-editor__notice {
    color: #e0a33c;
    background: #3a2f1a;
  }
}
</style>
